import React, { useEffect } from 'react';
import { Alert, Button, Typography } from '@mui/material';
import { Send, ArrowForward, Info } from '@mui/icons-material';
import { PatientObj } from 'pages/Dashboard/types/patient.types';
import * as spaUrls from 'utils/spaUrls';
import { trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';
import ResendInvite from 'pages/Dashboard/components/ResendInvite';

interface IInviteAlertProps {
  patient: PatientObj;
  hasExistingPhoneNumber: boolean;
}

interface IDifferentProviderPatientAlert extends IInviteAlertProps {
  callbackFn: () => void;
  isDemographicFlow: boolean;
  isOnboarded: boolean;
}

function DifferentProviderPatientAlert({
  patient,
  hasExistingPhoneNumber,
  isDemographicFlow,
  isOnboarded,
  callbackFn,
}: IDifferentProviderPatientAlert) {
  useEffect(() => {
    trackMixpanelEvent(MixpanelEventName.ADD_PATIENT_PATIENT_EXISTS_WITH_DIFFERENT_PROVIDER_SHOWN, {
      patient_id: patient.id ?? '',
      is_demographic_flow: isDemographicFlow,
      has_existing_phone_number: hasExistingPhoneNumber,
      is_onboarded: isOnboarded,
    });
  }, [patient.id, isDemographicFlow, hasExistingPhoneNumber, isOnboarded]);

  return (
    <Alert severity='warning' icon={<Info fontSize='inherit' />}>
      <Typography>
        A patient
        {' '}
        <Typography component='span' fontWeight={700}>
          already exists
        </Typography>
        {' '}
        with this
        {' '}
        <Typography component='span' fontWeight={700}>
          {hasExistingPhoneNumber ? 'mobile number' : 'email'}
        </Typography>
        {' '}
        at a
        {' '}
        <Typography component='span' fontWeight={700}>
          different
        </Typography>
        {' '}
        practice or provider.
      </Typography>
      <Button
        variant='contained'
        color='warning'
        sx={{
          mt: 1,
        }}
        endIcon={<ArrowForward />}
        onClick={callbackFn}
      >
        GOT IT
      </Button>
    </Alert>
  );
}

interface IOnboardedPatientAlertProps extends IInviteAlertProps {
  isDemographicFlow: boolean;
}

function OnboardedPatientAlert({
  patient,
  hasExistingPhoneNumber,
  isDemographicFlow,
}: IOnboardedPatientAlertProps) {
  const patientName = `${patient?.name?.firstName ?? ''} ${patient?.name?.lastName ?? ''}`.trim();

  useEffect(() => {
    trackMixpanelEvent(MixpanelEventName.ADD_PATIENT_PATIENT_EXISTS_ALERT_SHOWN, {
      patient_id: patient.id ?? '',
      is_demographic_flow: isDemographicFlow,
      has_existing_phone_number: hasExistingPhoneNumber,
      is_onboarded: true,
    });
  }, [patient.id, isDemographicFlow, hasExistingPhoneNumber]);

  const navigationToPatientDashboard = () => {
    window.open(
      spaUrls.mainNav.patientDetail(patient.id),
      '_blank',
    );
    trackMixpanelEvent(MixpanelEventName.ADD_PATIENT_VIEW_PATIENT_DASHBOARD_BUTTON_CLICKED, {
      patient_id: patient.id ?? '',
      is_demographic_flow: isDemographicFlow,
      has_existing_phone_number: hasExistingPhoneNumber,
      is_onboarded: true,
    });
  };

  return (
    <Alert severity='warning' icon={<Info fontSize='inherit' />}>
      <Typography>
        An active patient
        {' '}
        <Typography component='span' fontWeight={700} className='fs-mask'>
          {patientName ? `(${patientName}) ` : ''}
        </Typography>
        <Typography component='span' fontWeight={700}>
          already exists
        </Typography>
        {' '}
        with this
        {' '}
        <Typography component='span' fontWeight={700}>
          {hasExistingPhoneNumber ? 'mobile number' : 'email'}
        </Typography>
        . Would you like to view their dashboard?
      </Typography>
      <Button
        variant='contained'
        color='warning'
        sx={{
          mt: 1,
        }}
        endIcon={<ArrowForward />}
        onClick={navigationToPatientDashboard}
      >
        VIEW PATIENT DASHBOARD
      </Button>
    </Alert>
  );
}

interface IPendingOnboardedPatientAlert extends IInviteAlertProps {
  isDemographicFlow: boolean;
  callbackFn: () => void;
}

function PendingOnboardedPatientAlert({
  patient,
  hasExistingPhoneNumber,
  isDemographicFlow,
  callbackFn,
}: IPendingOnboardedPatientAlert) {
  const patientName = `${patient?.name?.firstName ?? ''} ${patient?.name?.lastName ?? ''}`.trim();
  const onResendInviteButtonClickHandler = (open: () => void) => {
    trackMixpanelEvent(MixpanelEventName.ADD_PATIENT_RESEND_INVITE_BUTTON_CLICKED, {
      patient_id: patient.id ?? '',
      is_demographic_flow: isDemographicFlow,
      has_existing_phone_number: hasExistingPhoneNumber,
      is_onboarded: false,
    });
    open();
  };

  useEffect(() => {
    trackMixpanelEvent(MixpanelEventName.ADD_PATIENT_PATIENT_EXISTS_ALERT_SHOWN, {
      patient_id: patient.id ?? '',
      is_demographic_flow: isDemographicFlow,
      has_existing_phone_number: hasExistingPhoneNumber,
      is_onboarded: false,
    });
  }, [patient.id, isDemographicFlow, hasExistingPhoneNumber]);

  return (
    <Alert severity='warning' icon={<Info fontSize='inherit' />}>
      <Typography>
        A patient
        {' '}
        <Typography component='span' fontWeight={700} className='fs-mask'>
          {patientName ? `(${patientName}) ` : ''}
        </Typography>
        <Typography component='span' fontWeight={700}>
          already exists
        </Typography>
        {' '}
        with this
        {' '}
        <Typography component='span' fontWeight={700}>
          {hasExistingPhoneNumber ? 'mobile number' : 'email'}
        </Typography>
        .
        Would you like to resend their invite?
      </Typography>
      <ResendInvite
        patient={patient}
        renderResendInviteButton={(open) => (
          <Button
            variant='contained'
            color='warning'
            sx={{
              mt: 1,
            }}
            startIcon={<Send />}
            onClick={() => onResendInviteButtonClickHandler(open)}
          >
            RESEND INVITE
          </Button>
        )}
        callbackFn={callbackFn}
      />
    </Alert>
  );
}

interface IPatientInviteAlertProps extends IInviteAlertProps {
  isOnboarded: boolean;
  hasSameProviderId: boolean;
  isDemographicFlow: boolean;
  callbackFn: () => void;
}

function PatientInviteAlert({
  isOnboarded,
  hasSameProviderId,
  ...rest
}: IPatientInviteAlertProps) {
  if (!hasSameProviderId) {
    return <DifferentProviderPatientAlert {...rest} isOnboarded={isOnboarded} />;
  }
  if (isOnboarded) {
    return <OnboardedPatientAlert {...rest} />;
  }

  return <PendingOnboardedPatientAlert {...rest} />;
}

export default PatientInviteAlert;
